import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { environment } from '../../../environments/environment'
import { retryOnServiceUnavailable } from '../utils/retry-on-unavailable'

/**
 * Narrowing accepted by GET /upload/files. Every field is applied server-side
 * so the count and the pages agree — the media library used to filter the
 * loaded page in the browser, which made "208 files" count videos on the
 * Images tab.
 */
export interface MediaFilesQuery {
  q?: string
  kind?: 'image' | 'video'
  /** Stored-key extensions; `jpg` also matches `jpeg` on the server. */
  ext?: string[]
  /** ISO instant — only files uploaded at or after it. */
  since?: string
  sortBy?: 'createdAt' | 'fileName' | 'size'
  sortDirection?: 'asc' | 'desc'
}

@Injectable({
  providedIn: 'root',
})
export class FoldersService {
  private http = inject(HttpClient)

  getUsage() {
    return this.http.get(`${environment.baseUrl}/upload/usage`)
  }

  getAllFiles(folderId: string, page = 1, limit = 60, query: MediaFilesQuery = {}) {
    const params: Record<string, string | number> = { page, limit }
    if (folderId) params['folderId'] = folderId
    if (query.q) params['q'] = query.q
    if (query.kind) params['kind'] = query.kind
    if (query.ext?.length) params['ext'] = query.ext.join(',')
    if (query.since) params['since'] = query.since
    if (query.sortBy) params['sortBy'] = query.sortBy
    if (query.sortDirection) params['sortDirection'] = query.sortDirection
    return this.http.get(`${environment.baseUrl}/upload/files`, { params })
  }

  // Folders
  getAllFolders() {
    // The media library renders the full folder tree — the API default of 10
    // silently hides folders beyond the first page.
    return this.http.get(`${environment.baseUrl}/upload/folders`, { params: { limit: 100 } })
  }

  addNewFolder(data: any) {
    return this.http.post(`${environment.baseUrl}/upload/folders`, data)
  }

  editFolder(id: string) {
    return this.http.patch(`${environment.baseUrl}/upload/folders/${id}`, {})
  }

  deleteFolder(id: string) {
    return this.http.delete(`${environment.baseUrl}/upload/folders/${id}`)
  }

  // Image uploads retry a 503 (object store briefly overloaded; nothing was
  // saved, so re-sending is safe). Videos do not: a re-send is up to 500 MB, and
  // the media library already offers a manual retry for them.
  uploadImage(data: any) {
    return this.http.post(`${environment.baseUrl}/upload/image`, data).pipe(retryOnServiceUnavailable())
  }

  uploadVideo(data: any) {
    return this.http.post(`${environment.baseUrl}/upload/video`, data)
  }

  /**
   * Same upload as `uploadImage`, but streams HttpEvents so callers can render
   * real byte-level progress instead of an indeterminate spinner.
   */
  uploadImageWithProgress(data: any) {
    return this.http
      .post(`${environment.baseUrl}/upload/image`, data, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(retryOnServiceUnavailable())
  }

  /** Progress-reporting variant of `uploadVideo` — see `uploadImageWithProgress`. */
  uploadVideoWithProgress(data: any) {
    return this.http.post(`${environment.baseUrl}/upload/video`, data, {
      reportProgress: true,
      observe: 'events',
    })
  }

  editImg(id: string, data: any) {
    return this.http.patch(`${environment.baseUrl}/upload/files/${id}`, data)
  }

  deleteImages(data: any) {
    return this.http.post(`${environment.baseUrl}/upload/files/delete`, data)
  }
}
